import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getPartialsKey, getShrimpMortalityKey } from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { useGetPartials } from 'modules/food/api/queries'

import { DEATH_REQUEST_CONFIG } from '.'
import { useGetDeadShrimpLineChart, useGetSummaryToday } from './queries'
import { CreateDeathVariables, EditDeathVariables } from './types'

export const useMutateCreateDeadShrimp = () => {
  const cycleApi = useCycleApi()
  const { refetch } = useGetSummaryToday()
  const { refetch: refetchLineChart } = useGetDeadShrimpLineChart()
  const { refetch: refetchPartials } = useGetPartials()

  return useMutation(
    (vars: CreateDeathVariables) => {
      const formData = new FormData()

      Object.entries(vars.body).forEach(([key, value]) => {
        if (value === undefined || value === null) return

        if (key === 'images') {
          ;(value as File[]).forEach((file) => {
            formData.append('images', file)
          })
        } else {
          formData.append(key, value as any)
        }
      })

      return cycleApi.request({
        ...DEATH_REQUEST_CONFIG.create,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    {
      onSuccess: () => {
        refetch()
        refetchLineChart()
        refetchPartials()
      },
    }
  )
}

export const useMutatePatchDeadShrimp = () => {
  const cycleApi = useCycleApi()
  const { refetch } = useGetSummaryToday()
  const { refetch: refetchLineChart } = useGetDeadShrimpLineChart()
  const { refetch: refetchPartials } = useGetPartials()

  return useMutation(
    ({ deathId, body }: EditDeathVariables) => {
      const formData = new FormData()

      Object.entries(body).forEach(([key, value]) => {
        if (value === undefined || value === null) return

        if (key === 'images') {
          ;(value as File[]).forEach((file) => {
            formData.append('images', file)
          })
        } else if (key === 'imageUrls') {
          ;(value as string[]).forEach((url) => {
            formData.append('imageUrls', url)
          })
        } else {
          formData.append(key, value as any)
        }
      })

      return cycleApi.request({
        ...DEATH_REQUEST_CONFIG.edit,
        url: `${DEATH_REQUEST_CONFIG.edit.url}/${deathId}`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    {
      onSuccess: () => {
        refetch()
        refetchLineChart()
        refetchPartials()
      },
    }
  )
}

export const useMutateDeleteDeadShrimp = () => {
  const cycleApi = useCycleApi()
  const queryClient = useQueryClient()

  return useMutation(
    (deathId: number) =>
      cycleApi.request({
        ...DEATH_REQUEST_CONFIG.delete,
        url: `${DEATH_REQUEST_CONFIG.delete.url}/${deathId}`,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getShrimpMortalityKey.all())
        queryClient.invalidateQueries(getPartialsKey.all())
      },
    }
  )
}

export const useMutateUploadDeadShrimpPhoto = () => {
  const cycleApi = useCycleApi()
  return useMutation((data: FormData) =>
    cycleApi.request({ ...DEATH_REQUEST_CONFIG.uploadImage, data })
  )
}
